import { Fragment, useMemo, useState } from 'react';

import type Account from '@/data/account/model';
import { getToken } from '@/shared/auth/auth';
import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import AppBranches from '@/shared/dev/branchSwitchers/AppBranches';
import LoginBranches from '@/shared/dev/branchSwitchers/LoginBranches';
import ReactBranches from '@/shared/dev/branchSwitchers/ReactBranches';
import { LaunchDarklyPanel } from '@/shared/dev/components/LaunchDarklyPanel';
import { RipplingDataExtractorPanel } from '@/shared/dev/components/RipplingDataExtractorPanel';
import { ServiceItem } from '@/shared/dev/components/ServiceItem';
import { ServicesConfig } from '@/shared/dev/serviceConfig';
import { Features } from '@/shared/features';
import { useBoolToggle } from '@/shared/hooks/useToggle';
import Button from '@/shared/ui/Button';
import FontIcon from '@/shared/ui/FontIcon';
import Environment from '@/shared/util/environment';
import { isWebview } from '@/shared/util/isWebview';
import { useWiwSelector, type WiwState } from '@/store';

import { Chip, Collapse, Modal, Paper, Tabs } from '@mantine/core';
import { useInterval } from '@mantine/hooks';
import Cookie from 'js-cookie';
import moment from 'moment-timezone';

import '@/shared/dev/styles/DevPanel.scss';

type Copyables = 'loginid' | 'userid' | 'userrole' | 'accountid' | 'planid' | 'authtoken' | 'stripe_id';

export const WIW_DEBUG_COOKIE_NAME = 'wiwDebug';

export default function DevPanel() {
  const currentUser = useWiwSelector(getAuthUser);
  const currentAccount = useWiwSelector(getAuthAccount);
  const loginId = useWiwSelector((state: WiwState) => state.auth.loginId);

  const [isDevPanelOpen, setIsDevPanelOpen] = useState(false);
  const [isCopied, setIsCopied] = useState<Record<Copyables, boolean>>({} as Record<Copyables, boolean>);
  const [reloadOnClose, setReloadOnClose] = useState(false);
  const [isAccountFeaturesOpen, toggle] = useBoolToggle(false);

  const [now, setNow] = useState(new Date());
  useInterval(() => setNow(new Date()), 1000 * 60, { autoInvoke: true });

  const machineTimeZone = useMemo(() => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }, []);

  const includeDate = useMemo(() => {
    const userDate = moment(now).format('YYYY-MM-DD');
    const accountDate = moment(now).tz(currentAccount.timezone_name).format('YYYY-MM-DD');
    const machineDate = moment(now).tz(machineTimeZone).format('YYYY-MM-DD');
    return userDate !== accountDate || userDate !== machineDate;
  }, [now, currentAccount, machineTimeZone]);

  const features = useMemo(() => {
    const features = Object.values(Features).reduce(
      (acc, feat) => {
        acc[feat] = false;
        return acc;
      },
      {} as Record<string, boolean>,
    );

    currentAccount.features.forEach(feat => {
      features[feat] = true;
    });

    return Object.entries(features).reduce(
      (acc, [feat, has]) => {
        if (has) {
          acc.has.push(feat);
        } else {
          acc.lacks.push(feat);
        }

        return acc;
      },
      { has: [], lacks: [] } as { has: string[]; lacks: string[] },
    );
  }, [currentAccount]);

  const toggleDevPanel = () => {
    if (reloadOnClose) {
      window.location.reload();
    }
    setIsDevPanelOpen(!isDevPanelOpen);
  };

  const devPanelShouldShow = () => {
    return !Environment.isProduction() || Cookie.get(WIW_DEBUG_COOKIE_NAME);
  };

  const onBranchChanged = () => {
    setReloadOnClose(true);
  };

  const buildVersionReporters = () => {
    return ServicesConfig.sort((a, b) => a.name.localeCompare(b.name)).map(serviceConfig => {
      return <ServiceItem key={serviceConfig.name} config={serviceConfig} onBranchChanged={onBranchChanged} />;
    });
  };

  const handleCopy = (label: Copyables, value: any) => {
    return async () => {
      await navigator.clipboard.writeText(value);
      setIsCopied({ ...isCopied, [label]: true });
      setTimeout(() => setIsCopied({ ...isCopied, [label]: false }), 1500);
    };
  };

  const checkBillingEngine = (account: Account) => {
    switch (true) {
      case account.isV5Billing():
        return '5 ( Stripe Billing )';
      case account.isADPBilling():
        return '4 ( ADP Billing )';
      default:
        return '1 ( WIW Billing )';
    }
  };

  const renderPanel = () => {
    const xrayLink = `https://x-ray.wiwdatastage.com/#/accounts/${currentAccount.id}/`;
    const stripeCustomerLink = Environment.isProduction()
      ? `https://dashboard.stripe.com/customers/${currentAccount.stripe_id}`
      : `https://dashboard.stripe.com/test/customers/${currentAccount.stripe_id}`;

    return (
      <Modal opened={isDevPanelOpen} onClose={toggleDevPanel} title="Dev Panel" className="dev-panel-dialog">
        {isWebview() && (
          <Button color="primary" className="mb-3" onClick={() => window.location.reload()}>
            Refresh Webview
          </Button>
        )}

        <Tabs defaultValue="general">
          <Tabs.List>
            <Tabs.Tab value="general">General</Tabs.Tab>
            <Tabs.Tab value="launchdarkly">LaunchDarkly</Tabs.Tab>
            <Tabs.Tab value="rippling-extractor">Rippling Extractor</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="general">
            <div className="row">
              <div className="col-md branchPickers">
                <div className="row">
                  {!Environment.isProduction() && [
                    <ReactBranches key="react" label="WIWJS Branch" onBranchChanged={onBranchChanged} />,
                    <LoginBranches key="login" label="Login-JS Branch" onBranchChanged={onBranchChanged} />,
                    <AppBranches key="monolith" label="App Branch" onBranchChanged={onBranchChanged} />,
                  ]}
                </div>
              </div>
            </div>

            <h3 className="list-title badge mt-3">Session Info</h3>
            <ul className="list-group">
              {Environment.isStaging() && (
                <li className="list-group-item">
                  X-Ray Link:&nbsp;
                  <a href={xrayLink} target="_blank" rel="noreferrer">
                    {xrayLink}
                  </a>
                </li>
              )}

              <li className="list-group-item d-flex justify-content-center" style={{ gap: '8px' }}>
                <span>
                  User Time:&nbsp;
                  <span className="badge badge-default">
                    {formatNativeTime(now, {
                      timeZone: currentUser.timezone_name,
                      dateStyle: includeDate ? 'short' : undefined,
                    })}
                  </span>
                </span>

                {currentUser.timezone_name !== currentAccount.timezone_name && (
                  <Fragment>
                    <span>|</span>

                    <span>
                      Account Time:&nbsp;
                      <span className="badge badge-default">
                        {formatNativeTime(now, {
                          timeZone: currentAccount.timezone_name,
                          dateStyle: includeDate ? 'short' : undefined,
                        })}
                      </span>
                    </span>
                  </Fragment>
                )}

                <span>|</span>

                <span>
                  Machine Time:&nbsp;
                  <span className="badge badge-default">
                    {formatNativeTime(now, {
                      dateStyle: includeDate ? 'short' : undefined,
                    })}
                  </span>
                </span>
              </li>

              <li className="list-group-item d-flex align-items-baseline justify-content-between">
                <div>
                  Current Login ID:
                  <span className="badge badge-default">{loginId}</span>
                </div>

                <Button color="secondary" onClick={handleCopy('loginid', loginId)} size="sm">
                  <FontIcon icon="copy" /> {isCopied.loginid ? 'Copied!' : 'Copy Login ID'}
                </Button>
              </li>

              <li className="list-group-item d-flex align-items-baseline justify-content-between">
                <div>
                  Current User ID:
                  <span className="badge badge-default">{currentUser.id}</span>
                </div>

                <Button color="secondary" onClick={handleCopy('userid', currentUser.id)} size="sm">
                  <FontIcon icon="copy" /> {isCopied.userid ? 'Copied!' : 'Copy User ID'}
                </Button>
              </li>

              <li className="list-group-item d-flex align-items-baseline justify-content-between">
                <div>
                  Current User Role:
                  <span className="badge badge-default">{currentUser.roleName}</span>
                </div>

                <Button color="secondary" onClick={handleCopy('userrole', currentUser.role)} size="sm">
                  <FontIcon icon="copy" /> {isCopied.userrole ? 'Copied!' : 'Copy User Role'}
                </Button>
              </li>

              <li className="list-group-item d-flex align-items-baseline justify-content-between">
                <div>
                  Current Account ID:
                  <span className="badge badge-default">{currentAccount.id}</span>
                </div>

                <Button color="secondary" onClick={handleCopy('accountid', currentAccount.id)} size="sm">
                  <FontIcon icon="copy" /> {isCopied.accountid ? 'Copied!' : 'Copy Account ID'}
                </Button>
              </li>

              <li className="list-group-item d-flex align-items-baseline justify-content-between">
                <div>
                  Current Account Plan:
                  <span className="badge badge-default">{currentAccount.plan_id}</span>
                </div>

                <Button color="secondary" onClick={handleCopy('planid', currentAccount.plan_id)} size="sm">
                  <FontIcon icon="copy" /> {isCopied.planid ? 'Copied!' : 'Copy Account Plan'}
                </Button>
              </li>

              <li className="list-group-item d-flex align-items-baseline justify-content-between">
                <div>
                  Current Auth Token:
                  <span className="badge badge-default">TL;DR</span>
                </div>

                <Button color="secondary" onClick={handleCopy('authtoken', getToken())} size="sm">
                  <FontIcon icon="copy" /> {isCopied.authtoken ? 'Copied!' : 'Copy Auth Token'}
                </Button>
              </li>

              <li className="list-group-item d-flex align-items-baseline justify-content-between">
                <div>
                  Current Billing Engine:
                  <span className="badge badge-default">{checkBillingEngine(currentAccount)}</span>
                </div>
              </li>

              <li className="list-group-item d-flex align-items-baseline justify-content-between">
                <div>
                  Current Stripe ID:
                  <span className="badge badge-default">
                    {currentAccount.stripe_id ? (
                      <a target="_blank" href={stripeCustomerLink} rel="noopener noreferrer">
                        {currentAccount.stripe_id}
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </span>
                </div>

                {currentAccount.stripe_id && (
                  <Button color="secondary" onClick={handleCopy('stripe_id', currentAccount.stripe_id)} size="sm">
                    <FontIcon icon="copy" /> {isCopied.stripe_id ? 'Copied!' : 'Copy Stripe ID'}
                  </Button>
                )}
              </li>

              {CONFIG.DEPLOY.SHA && (
                <li className="list-group-item justify-content-between">
                  Current Deploy:
                  <span className="badge badge-default">
                    <a
                      target="_blank"
                      href={`${CONFIG.DEVPANEL.repositoryBase}/commit/${CONFIG.DEPLOY.SHA}`}
                      rel="noreferrer"
                    >
                      {CONFIG.DEPLOY.SHA.substring(0, 8)}
                    </a>
                  </span>
                </li>
              )}
            </ul>

            <div className="account-features">
              <h3 className="list-title badge" onClick={toggle}>
                Account Features
                {isAccountFeaturesOpen ? <FontIcon icon="chevron-up" /> : <FontIcon icon="chevron-down" />}
              </h3>

              <Collapse in={isAccountFeaturesOpen}>
                <div className="account-features__collapsable">
                  <div className="account-features__list-container">
                    <Paper className="account-features__list">
                      {features.has.length === 0 && 'You have no features somehow ... sus'}

                      {features.has.length > 0 &&
                        features.has
                          .toSorted((a, b) => a.localeCompare(b))
                          .map(feat => (
                            <Chip key={feat} variant="light" size="xs" radius="sm" checked={true}>
                              {feat}
                            </Chip>
                          ))}
                    </Paper>
                  </div>

                  <div className="account-features__list-container">
                    <Paper className="account-features__list">
                      {features.lacks.length === 0 && (
                        <li className="list-group-item">You have all the features! 🎉</li>
                      )}

                      {features.lacks.length > 0 &&
                        features.lacks
                          .toSorted((a, b) => a.localeCompare(b))
                          .map(feat => (
                            <Chip key={feat} variant="light" size="xs" radius="sm" checked={false}>
                              {feat}
                            </Chip>
                          ))}
                    </Paper>
                  </div>
                </div>
              </Collapse>
            </div>

            <h3 className="list-title badge mt-3">Services</h3>
            <ul className="list-group d-flex" data-testid="servicesList">
              {buildVersionReporters()}
            </ul>
          </Tabs.Panel>

          <Tabs.Panel value="launchdarkly">
            <LaunchDarklyPanel />
          </Tabs.Panel>

          <Tabs.Panel value="rippling-extractor">
            <RipplingDataExtractorPanel />
          </Tabs.Panel>
        </Tabs>
      </Modal>
    );
  };

  const renderButton = () => {
    return (
      // biome-ignore lint/a11y/noStaticElementInteractions: just not changing this right now, or ever
      <div
        className="dev-panel-link"
        onClick={toggleDevPanel}
        style={{
          position: 'absolute',
          top: 56,
          right: 0,
          padding: 10,
        }}
      >
        <button type="button" className="border-0 bg-transparent">
          <FontIcon icon="settings" />
        </button>
      </div>
    );
  };

  return (
    <div className="container dev-panel">
      {devPanelShouldShow() && isDevPanelOpen && renderPanel()}
      {devPanelShouldShow() && renderButton()}
    </div>
  );
}

function formatNativeTime(date: Date, options?: Intl.DateTimeFormatOptions) {
  return Intl.DateTimeFormat(undefined, { timeStyle: 'short', ...options })
    .format(date)
    .toLowerCase()
    .replace(/(:00)?\s?([ap])m/, '$2');
}
